import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/config";
import { LogoWinarq } from "../components/LogoWinarq";

const O="#D2611A", BK="#1C1917";
const W={bg:"#FAF9F7",s1:"#FFFFFF",s2:"#F4F2EF",border:"#E8E4DE",off:"#3D3830",muted:"#8C8078",
  sans:"'Outfit','Helvetica Neue',Arial,sans-serif",mono:"'IBM Plex Mono',monospace"};

const FONTS=`
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
body{-webkit-font-smoothing:antialiased}
@keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
`;

export default function AuthPage() {
  const navigate = useNavigate();
  const { signIn, signUp } = useAuth();
  const [tab, setTab] = useState("login"); // login | registro | pendiente
  const [form, setForm] = useState({ email:"", password:"", nombre:"", empresa:"", rubro:"", telefono:"" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = k => v => setForm(p => ({...p,[k]:v}));

  const handleLogin = async () => {
    if (!form.email || !form.password) { setError("Completá email y contraseña."); return; }
    setLoading(true); setError("");
    try {
      await signIn(form.email, form.password);
      navigate("/app");
    } catch(e) {
      setError(e.code==="auth/invalid-credential"||e.code==="auth/wrong-password"
        ? "Email o contraseña incorrectos."
        : e.code==="auth/user-not-found"
        ? "No existe una cuenta con ese email."
        : "Error al ingresar. Verificá tus datos.");
    }
    setLoading(false);
  };

  const handleRegistro = async () => {
    if (!form.email||!form.password||!form.nombre) { setError("Nombre, email y contraseña son obligatorios."); return; }
    if (form.password.length < 6) { setError("La contraseña debe tener al menos 6 caracteres."); return; }
    setLoading(true); setError("");
    try {
      const cred = await signUp(form.email, form.password);
      // Guardar en Firestore como pendiente de aprobación
      await setDoc(doc(db,"usuarios",cred.user.uid), {
        uid:        cred.user.uid,
        email:      form.email,
        nombre:     form.nombre,
        empresa:    form.empresa||"",
        rubro:      form.rubro||"",
        telefono:   form.telefono||"",
        rol:        "comprador",
        estado:     "pendiente", // pendiente | activo | bloqueado
        creadoEn:   serverTimestamp(),
        updatedAt:  serverTimestamp(),
      });
      // También en invitados para que el admin lo vea
      await setDoc(doc(db,"usuarios_invitados",cred.user.uid), {
        uid: cred.user.uid, email:form.email, nombre:form.nombre,
        empresa:form.empresa||"", telefono:form.telefono||"",
        creadoEn: serverTimestamp(),
      });
      setTab("pendiente");
    } catch(e) {
      setError(e.code==="auth/email-already-in-use"
        ? "Ya existe una cuenta con ese email. Iniciá sesión."
        : "Error al registrarse. Intentá de nuevo.");
    }
    setLoading(false);
  };

  const inp = {
    width:"100%", background:"transparent", border:"none",
    borderBottom:`1.5px solid ${W.border}`, color:BK,
    fontFamily:W.sans, fontSize:14, padding:"10px 0", outline:"none",
  };
  const lbl = { fontFamily:W.sans,fontSize:9,letterSpacing:2,textTransform:"uppercase",color:W.muted,marginBottom:6,fontWeight:600,display:"block" };
  const btn = (bg,fg="#fff") => ({
    width:"100%", padding:"13px 0", background:bg, color:fg,
    fontFamily:W.sans, fontSize:10, letterSpacing:2, textTransform:"uppercase",
    border:"none", cursor:"pointer", fontWeight:700, transition:"opacity .2s",
  });

  return (
    <div style={{minHeight:"100vh",background:BK,display:"flex",fontFamily:W.sans}}>
      <style>{FONTS}</style>

      {/* LEFT — imagen + copy */}
      <div style={{flex:1,position:"relative",display:"flex",flexDirection:"column",justifyContent:"flex-end",padding:48,background:"linear-gradient(135deg, #1C1917 60%, #2A1F14)"}}>
        <div style={{position:"absolute",top:0,left:0,right:0,bottom:0,backgroundImage:"url('https://images.unsplash.com/photo-1556909172-54557c7e4fb7?w=800&q=70')",backgroundSize:"cover",backgroundPosition:"center",opacity:.12}}/>
        <div style={{position:"relative"}}>
          <div onClick={()=>navigate("/")} style={{cursor:"pointer",marginBottom:48}}>
            <LogoWinarq height={44} dark={false}/>
          </div>
          <div style={{fontFamily:W.mono,fontSize:9,color:O,letterSpacing:2.5,textTransform:"uppercase",marginBottom:16}}>
            Importación directa · China → Argentina
          </div>
          <h1 style={{fontFamily:W.sans,fontSize:"clamp(24px,3vw,42px)",fontWeight:800,color:"#fff",lineHeight:1.1,marginBottom:16}}>
            Acceso a precios<br/><span style={{fontWeight:200,color:"rgba(255,255,255,.4)"}}>de fábrica real.</span>
          </h1>
          <p style={{fontFamily:W.sans,fontSize:13,color:"rgba(255,255,255,.4)",lineHeight:1.8,maxWidth:340,marginBottom:40,fontWeight:300}}>
            Registrate para ver precios, calcular costos con el flete actual y cotizar tu próxima obra.
          </p>
          {/* Beneficios */}
          {["Precios con IVA incluido","Calculadora de costos en tiempo real","Catálogo completo con especificaciones","Soporte directo por WhatsApp"].map(b=>(
            <div key={b} style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
              <div style={{width:20,height:20,background:O,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <span style={{color:"#fff",fontSize:10,fontWeight:700}}>✓</span>
              </div>
              <span style={{fontFamily:W.sans,fontSize:12,color:"rgba(255,255,255,.55)",fontWeight:300}}>{b}</span>
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT — form */}
      <div style={{width:460,background:W.bg,display:"flex",flexDirection:"column",justifyContent:"center",padding:"48px 44px",overflowY:"auto"}}>

        {tab === "pendiente" ? (
          <div style={{textAlign:"center",animation:"fadeUp .6s ease both"}}>
            <div style={{fontSize:56,marginBottom:20,animation:"fadeUp .4s ease both"}}>✉</div>
            <div style={{fontFamily:W.sans,fontSize:24,fontWeight:800,color:BK,marginBottom:12}}>
              Solicitud enviada
            </div>
            <div style={{fontFamily:W.sans,fontSize:13,color:W.muted,lineHeight:1.75,marginBottom:32,fontWeight:300}}>
              Recibimos tu solicitud de acceso. La revisamos manualmente para garantizar la calidad del servicio.<br/><br/>
              <strong style={{color:BK}}>Te contactamos en menos de 24 horas hábiles</strong> para confirmar tu acceso.
            </div>
            <div style={{background:"#FEF3E8",border:`1.5px solid ${O}`,padding:"16px 20px",marginBottom:28,textAlign:"left"}}>
              <div style={{fontFamily:W.sans,fontSize:11,color:O,fontWeight:700,marginBottom:4}}>Mientras tanto podés...</div>
              <div style={{fontFamily:W.sans,fontSize:12,color:W.off,lineHeight:1.7,fontWeight:300}}>
                • Ver el catálogo público en <span style={{color:O,cursor:"pointer",fontWeight:600}} onClick={()=>navigate("/catalogo")}>winarq.com.ar/catalogo</span><br/>
                • Consultar por WhatsApp: <a href="https://wa.me/5491150614489" style={{color:O,fontWeight:600}}>+54 9 11 5061-4489</a>
              </div>
            </div>
            <button onClick={()=>navigate("/")} style={{...btn(BK),borderRadius:2}}>
              Volver al inicio →
            </button>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div style={{display:"flex",marginBottom:36,borderBottom:`1px solid ${W.border}`}}>
              {[["login","Ingresar"],["registro","Solicitar acceso"]].map(([t,l])=>(
                <button key={t} onClick={()=>{setTab(t);setError("");}}
                  style={{flex:1,padding:"14px 0",fontFamily:W.sans,fontSize:10,letterSpacing:2,textTransform:"uppercase",fontWeight:tab===t?700:400,color:tab===t?O:W.muted,background:"transparent",border:"none",borderBottom:tab===t?`2px solid ${O}`:"2px solid transparent",cursor:"pointer",marginBottom:-1}}>
                  {l}
                </button>
              ))}
            </div>

            {tab === "login" && (
              <div style={{animation:"fadeUp .4s ease both"}}>
                <div style={{fontFamily:W.sans,fontSize:22,fontWeight:800,color:BK,marginBottom:4}}>Bienvenido</div>
                <div style={{fontFamily:W.sans,fontSize:12,color:W.muted,marginBottom:32,fontWeight:300}}>Ingresá con tu cuenta de WINARQ</div>

                <div style={{marginBottom:20}}>
                  <label style={lbl}>Email</label>
                  <input type="email" value={form.email} onChange={e=>set("email")(e.target.value)} placeholder="tu@email.com" style={inp}
                    onFocus={e=>e.target.style.borderBottomColor=O} onBlur={e=>e.target.style.borderBottomColor=W.border}
                    onKeyDown={e=>e.key==="Enter"&&handleLogin()}/>
                </div>
                <div style={{marginBottom:28}}>
                  <label style={lbl}>Contraseña</label>
                  <input type="password" value={form.password} onChange={e=>set("password")(e.target.value)} placeholder="••••••••" style={inp}
                    onFocus={e=>e.target.style.borderBottomColor=O} onBlur={e=>e.target.style.borderBottomColor=W.border}
                    onKeyDown={e=>e.key==="Enter"&&handleLogin()}/>
                </div>

                {error&&<div style={{fontFamily:W.sans,fontSize:11,color:"#DC2626",marginBottom:16,padding:"10px 12px",background:"#FEF2F2",border:"1px solid #DC262633"}}>{error}</div>}

                <button onClick={handleLogin} disabled={loading} style={{...btn(O),marginBottom:16,opacity:loading?.7:1}}>
                  {loading?"Ingresando...":"Ingresar →"}
                </button>
                <button onClick={()=>{setTab("registro");setError("");}}
                  style={{...btn("transparent",W.muted),border:`1.5px solid ${W.border}`}}>
                  No tengo cuenta — Solicitar acceso
                </button>
              </div>
            )}

            {tab === "registro" && (
              <div style={{animation:"fadeUp .4s ease both"}}>
                <div style={{fontFamily:W.sans,fontSize:22,fontWeight:800,color:BK,marginBottom:4}}>Solicitar acceso</div>
                <div style={{fontFamily:W.sans,fontSize:12,color:W.muted,marginBottom:28,fontWeight:300}}>
                  Revisamos cada solicitud manualmente — acceso en menos de 24 horas.
                </div>

                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
                  <div>
                    <label style={lbl}>Nombre *</label>
                    <input value={form.nombre} onChange={e=>set("nombre")(e.target.value)} placeholder="Arq. Juan García" style={inp}
                      onFocus={e=>e.target.style.borderBottomColor=O} onBlur={e=>e.target.style.borderBottomColor=W.border}/>
                  </div>
                  <div>
                    <label style={lbl}>Empresa / Estudio</label>
                    <input value={form.empresa} onChange={e=>set("empresa")(e.target.value)} placeholder="García Arquitectura" style={inp}
                      onFocus={e=>e.target.style.borderBottomColor=O} onBlur={e=>e.target.style.borderBottomColor=W.border}/>
                  </div>
                </div>
                <div style={{marginBottom:16}}>
                  <label style={lbl}>Email *</label>
                  <input type="email" value={form.email} onChange={e=>set("email")(e.target.value)} placeholder="tu@email.com" style={inp}
                    onFocus={e=>e.target.style.borderBottomColor=O} onBlur={e=>e.target.style.borderBottomColor=W.border}/>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
                  <div>
                    <label style={lbl}>WhatsApp</label>
                    <input value={form.telefono} onChange={e=>set("telefono")(e.target.value)} placeholder="+54 9 11..." style={inp}
                      onFocus={e=>e.target.style.borderBottomColor=O} onBlur={e=>e.target.style.borderBottomColor=W.border}/>
                  </div>
                  <div>
                    <label style={lbl}>¿Qué importás?</label>
                    <input value={form.rubro} onChange={e=>set("rubro")(e.target.value)} placeholder="Pisos, sanitarios..." style={inp}
                      onFocus={e=>e.target.style.borderBottomColor=O} onBlur={e=>e.target.style.borderBottomColor=W.border}/>
                  </div>
                </div>
                <div style={{marginBottom:28}}>
                  <label style={lbl}>Contraseña *</label>
                  <input type="password" value={form.password} onChange={e=>set("password")(e.target.value)} placeholder="Mínimo 6 caracteres" style={inp}
                    onFocus={e=>e.target.style.borderBottomColor=O} onBlur={e=>e.target.style.borderBottomColor=W.border}/>
                </div>

                {error&&<div style={{fontFamily:W.sans,fontSize:11,color:"#DC2626",marginBottom:16,padding:"10px 12px",background:"#FEF2F2",border:"1px solid #DC262633"}}>{error}</div>}

                <button onClick={handleRegistro} disabled={loading} style={{...btn(O),marginBottom:12,opacity:loading?.7:1}}>
                  {loading?"Enviando solicitud...":"Solicitar acceso →"}
                </button>
                <div style={{fontFamily:W.sans,fontSize:10,color:W.muted,textAlign:"center",lineHeight:1.6}}>
                  Al registrarte aceptás recibir información comercial de WINARQ.
                </div>
              </div>
            )}
          </>
        )}

        {/* Footer del form */}
        <div style={{marginTop:32,paddingTop:20,borderTop:`1px solid ${W.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div onClick={()=>navigate("/")} style={{fontFamily:W.sans,fontSize:10,color:W.muted,cursor:"pointer",letterSpacing:1,textTransform:"uppercase",fontWeight:500}}
            onMouseEnter={e=>e.target.style.color=O} onMouseLeave={e=>e.target.style.color=W.muted}>
            ← Volver al inicio
          </div>
          <a href="https://wa.me/5491150614489" target="_blank" rel="noreferrer"
            style={{fontFamily:W.sans,fontSize:10,color:O,textDecoration:"none",letterSpacing:1,textTransform:"uppercase",fontWeight:600}}>
            ↗ WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}
